// Hook personalizado usePermissions
// Proporciona funciones para verificar permisos del usuario actual

import { useAuth } from './useAuth'
import { hasPermission, FIXED_ROLES } from '../constants/permissions'

/**
 * Hook para verificar permisos del usuario actual
 * @returns {Object} Objeto con funciones para verificar permisos
 */
export const usePermissions = () => {
  const { userData, customRole } = useAuth()

  const normalizeRole = (role) => {
    if (!role || typeof role !== 'string') return role
    const normalized = role.toLowerCase().trim()
    const compact = normalized.replace(/\s+/g, '').replace(/_/g, '')
    if (compact === 'superadmin' || compact === 'superadministrador') return FIXED_ROLES.SUPERADMIN
    if (compact === 'admin' || compact === 'administrador') return FIXED_ROLES.ADMIN
    if (compact === 'cliente' || compact === 'user' || compact === 'usuario') return FIXED_ROLES.CLIENT
    return role
  }

  /**
   * Obtener el rol efectivo del usuario
   * @returns {string|Object} Rol del usuario (fijo o personalizado)
   */
  const getUserRole = () => {
    if (!userData) return null
    
    const role = normalizeRole(userData.role)
    
    // Si es un rol fijo, retornar el string
    if (role === FIXED_ROLES.SUPERADMIN || role === FIXED_ROLES.ADMIN || role === FIXED_ROLES.CLIENT) {
      return role
    }
    
    // Si es un rol personalizado, retornar el objeto del rol
    if (customRole) {
      return customRole
    }
    
    // Si no hay customRole pero tiene roleId, retornar el role como string
    // (el rol se cargará después)
    return role
  }

  /**
   * Verificar si el usuario tiene un permiso específico
   * @param {string} module - Módulo a verificar
   * @param {string} action - Acción a verificar
   * @returns {boolean}
   */
  const can = (module, action) => {
    const role = getUserRole()
    if (!role) return false
    
    return hasPermission(role, module, action)
  }

  /**
   * Verificar si el usuario es superadmin
   * @returns {boolean}
   */
  const isSuperAdmin = () => {
    return normalizeRole(userData?.role) === FIXED_ROLES.SUPERADMIN
  }

  /**
   * Verificar si el usuario es admin
   * @returns {boolean}
   */
  const isAdmin = () => {
    return normalizeRole(userData?.role) === FIXED_ROLES.ADMIN
  }

  /**
   * Verificar si el usuario tiene acceso al panel admin
   * @returns {boolean}
   */
  const hasAdminAccess = () => {
    const role = getUserRole()
    if (!role) return false
    
    // Cliente no tiene acceso
    if (role === FIXED_ROLES.CLIENT) return false
    
    // Superadmin y admin tienen acceso
    if (role === FIXED_ROLES.SUPERADMIN || role === FIXED_ROLES.ADMIN) return true
    
    // Roles personalizados: verificar si tienen al menos un permiso
    if (typeof role === 'object' && role.permissions) {
      return Object.keys(role.permissions).length > 0
    }
    
    return false
  }

  return {
    can,
    isSuperAdmin,
    isAdmin,
    hasAdminAccess,
    getUserRole,
    getNormalizedRole: () => normalizeRole(userData?.role),
    userRole: getUserRole(),
    customRole
  }
}

export default usePermissions

